import { LOCATION_CHANGE } from 'connected-react-router';
import { createMiddleware } from '@shared/redux/create-middleware';
import { getApiErrorKeyByException } from '@shared/api/api.utils';
import { showErrorToast, showSuccessToast } from '@shared/toasts/toasts.state';
import { match } from '@shared/utils/match';
import { Routes } from '@domain/router';
import { ERROR_MESSAGES } from '@shared/constants/validation.constants';
import {
    DELETE_INVITE_REQUEST_FAILED,
    DELETE_USER_REQUEST_FAILED,
    EDIT_MEMBER_ROLE_REQUEST_FAILED,
    GET_TEAM_INVITES_REQUEST_FAILED,
    GET_TEAM_MEMBERS_REQUEST_FAILED,
    SEND_INVITE_REQUEST_FAILED,
    SEND_INVITE_REQUEST_SUCCEEDED,
    DELETE_INVITE_REQUEST_SUCCEEDED,
    DELETE_USER_REQUEST_SUCCEEDED,
    OPEN_DELETE_MODAL_FAILED,
    OPEN_INVITE_MODAL_FAILED,
    INVITEE_EMAIL_VALIDATE,
    inviteeEmailNotValid,
    updateInviteeEmailError,
    getTeamMembers,
    getTeamInviteMembers,
    getTeamClassification,
    handleInvalidRoleError,
    EDIT_MEMBER_ROLE_REQUEST_SUCCEEDED,
} from './team.screen.state';
import {
    GENERIC_TEAM_SCREEN_MODAL_TEXTS, SEND_INVITE_API_ERROR,
} from './team-modals.constants';
import { inviteeEmailValidate } from './team-modals.validation';
import { TEAM_TYPE, TOAST_TEAM } from './team.constants';

const handleApiRaisedErrors = {
    [SEND_INVITE_API_ERROR.EMAIL_ALREADY_INVITED]: () => showErrorToast({
        message: GENERIC_TEAM_SCREEN_MODAL_TEXTS
            .EMAIL_ALREADY_INVITED_MESSAGE,
    }),
    [SEND_INVITE_API_ERROR.INVITEE_IS_ALREADY_A_MEMBER]: () => showErrorToast({
        message: GENERIC_TEAM_SCREEN_MODAL_TEXTS
            .INVITEE_IS_ALREADY_A_MEMBER,
    }),
    [SEND_INVITE_API_ERROR.INVITEE_OWN_EMAIL]: () => showErrorToast({
        message: GENERIC_TEAM_SCREEN_MODAL_TEXTS
            .INVITEE_OWN_EMAIL,
    }),
    [SEND_INVITE_API_ERROR.INVITEE_ALREADY_IN_A_TEAM]: () => showErrorToast({
        message: GENERIC_TEAM_SCREEN_MODAL_TEXTS
            .INVITEE_ALREADY_IN_A_TEAM,
    }),
    [SEND_INVITE_API_ERROR.INVALID_ROLE]: () => handleInvalidRoleError(
        GENERIC_TEAM_SCREEN_MODAL_TEXTS.INVALID_ROLE_ERROR_MESSAGE,
    ),
    [SEND_INVITE_API_ERROR.INVALID_EMAIL_AND_INVALID_ROLE]: () => handleInvalidRoleError(
        GENERIC_TEAM_SCREEN_MODAL_TEXTS.INVALID_ROLE_ERROR_MESSAGE,
    ),
    [SEND_INVITE_API_ERROR.BLANK_EMAIL]: () => updateInviteeEmailError({
        error: { message: ERROR_MESSAGES.INVALID_EMAIL },
    }),
    [SEND_INVITE_API_ERROR.BLANK_FIELDS]: () => [
        updateInviteeEmailError({ error: { message: ERROR_MESSAGES.INVALID_EMAIL } }),
        handleInvalidRoleError(GENERIC_TEAM_SCREEN_MODAL_TEXTS.INVALID_ROLE_ERROR_MESSAGE),
    ],
    [SEND_INVITE_API_ERROR.INVALID_EMAIL]: () => null,
};

const handleError = (action) => {
    const { response } = action.payload;
    const errorKey = getApiErrorKeyByException(action.payload);
    const errorValue = response.data.errors[errorKey];

    return showErrorToast({
        message: errorValue,
    });
};

const handleSendInviteFailed = (action) => {
    const { response } = action.payload;
    const errorKey = getApiErrorKeyByException(action.payload);
    const errorMessage = response.data.errors[errorKey];

    if (errorMessage && handleApiRaisedErrors[errorMessage]) {
        return handleApiRaisedErrors[errorMessage]();
    }
    return handleError(action);
};


const handleSendInviteSucceeded = () => showSuccessToast({
    message: GENERIC_TEAM_SCREEN_MODAL_TEXTS.SEND_INVITE_SUCCEEDED,
});

const handleInviteeEmailValidation = action => inviteeEmailValidate({
    data: action.payload,
})
    .then(() => updateInviteeEmailError({
        error: { message: null },
    }))
    .catch(({ email }) => inviteeEmailNotValid({
        error: { message: email },
    }));

const handleSuccessedDelete = (type, action) => {
    if (type === TEAM_TYPE.INVITE.value) {
        return showSuccessToast({
            message: GENERIC_TEAM_SCREEN_MODAL_TEXTS.DELETE_INVITE_SUCCESS_MESSAGE,
        });
    }
    if (type === TEAM_TYPE.MEMBER.value) {
        const { username } = action.requestAction.payload;
        const formattedMessage = GENERIC_TEAM_SCREEN_MODAL_TEXTS
            .DELETE_USER_SUCCESS_MESSAGE.replace('{username}', username);
        return showSuccessToast({
            message: formattedMessage,
        });
    }
    return null;
};

const handleOpenDeleteModalFailed = () => showErrorToast({
    message: GENERIC_TEAM_SCREEN_MODAL_TEXTS.OPEN_DELETE_MODAL_FAILED,
});

const handleOpenInviteModalFailed = () => showErrorToast({
    message: GENERIC_TEAM_SCREEN_MODAL_TEXTS.OPEN_INVITE_MODAL_FAILED,
});

export const teamMembersScreenMiddlewares = createMiddleware({
    [LOCATION_CHANGE]: match((_, action) => action.payload.location.pathname, {
        [Routes.TEAM]: () => [
            getTeamMembers(),
            getTeamInviteMembers(),
            getTeamClassification(),
        ],
    }),
    [DELETE_INVITE_REQUEST_FAILED]: (_, action) => handleError(action),
    [DELETE_USER_REQUEST_FAILED]: (_, action) => handleError(action),
    [EDIT_MEMBER_ROLE_REQUEST_FAILED]: (_, action) => handleError(action),
    [EDIT_MEMBER_ROLE_REQUEST_SUCCEEDED]: (_, action) => {
        const memberName = action.payload.data.name;
        const includeUserName = TOAST_TEAM.EDIT_MEMBER_ROLE_SUCCEEDED.replace('$MemberName', memberName);
        return showSuccessToast({ message: includeUserName });
    },
    [GET_TEAM_INVITES_REQUEST_FAILED]: (_, action) => handleError(action),
    [GET_TEAM_MEMBERS_REQUEST_FAILED]: (_, action) => handleError(action),
    [SEND_INVITE_REQUEST_FAILED]: (_, action) => handleSendInviteFailed(action),
    [SEND_INVITE_REQUEST_SUCCEEDED]: handleSendInviteSucceeded,
    [INVITEE_EMAIL_VALIDATE]: (_, action) => handleInviteeEmailValidation(action),
    [DELETE_INVITE_REQUEST_SUCCEEDED]: (_, action) => handleSuccessedDelete(
        TEAM_TYPE.INVITE.value, action,
    ),
    [DELETE_USER_REQUEST_SUCCEEDED]: (_, action) => handleSuccessedDelete(
        TEAM_TYPE.MEMBER.value, action,
    ),
    [OPEN_DELETE_MODAL_FAILED]: handleOpenDeleteModalFailed,
    [OPEN_INVITE_MODAL_FAILED]: handleOpenInviteModalFailed,
});
